import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  ShoppingCart, 
  FileText, 
  Truck, 
  CreditCard, 
  Star,
  CheckCircle,
  Clock
} from 'lucide-react'

interface WorkflowStep {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  status: 'completed' | 'current' | 'pending'
  route: string
}

interface WorkflowProgressProps {
  userRole: 'BUYER' | 'VENDOR'
}

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({ userRole }) => {
  const navigate = useNavigate()
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([])
  const [loading, setLoading] = useState(true)
  const [completionPercentage, setCompletionPercentage] = useState(0)
  const [lastFetch, setLastFetch] = useState<number>(0)
  const [cache, setCache] = useState<any>(null)

  // Cache statistics for 30 seconds to reduce API calls
  const CACHE_DURATION = 30 * 1000 // 30 seconds

  useEffect(() => {
    loadWorkflowProgress()
  }, [userRole])



  const loadWorkflowProgress = async () => {
    try {
      // Check cache first
      const now = Date.now()
      if (cache && (now - lastFetch) < CACHE_DURATION) {
        // Use cached data if available and not expired
        setWorkflowSteps(cache.steps)
        setCompletionPercentage(cache.completionPercentage)
        setLoading(false)
        return
      }

      // Debug: Check authentication state
      const { accessToken, isAuthenticated, user } = useAuthStore.getState()
      console.log('🔐 Auth Debug:', {
        isAuthenticated,
        hasToken: !!accessToken,
        userEmail: user?.email,
        tokenLength: accessToken?.length || 0
      })

      setLoading(true)
      
      // Get current workflow status - ensure all URLs have /api prefix
      const [poStats, agreementStats, fulfillmentStats, invoiceStats] = await Promise.all([
        apiService.get('/api/post-quote-activity/purchase-orders/statistics'),
        apiService.get('/api/post-quote-activity/digital-agreements/statistics'),
        apiService.get('/api/post-quote-activity/fulfillments/statistics'),
        apiService.get('/api/post-quote-activity/invoices/statistics')
      ])

      // Debug: Log the received data
      console.log('🔍 WorkflowProgress Debug Data:');
      console.log('PO Stats:', poStats);
      console.log('PO Stats Type:', typeof poStats);
      console.log('PO Stats Keys:', Object.keys(poStats || {}));
      console.log('Agreement Stats:', agreementStats);
      console.log('Agreement Stats Type:', typeof agreementStats);
      console.log('Agreement Stats Keys:', Object.keys(agreementStats || {}));
      console.log('Agreement Stats String Content:', agreementStats);
      console.log('Agreement Stats String Length:', typeof agreementStats === 'string' ? agreementStats.length : 'N/A');
      console.log('Fulfillment Stats:', fulfillmentStats);
      console.log('Invoice Stats:', invoiceStats);

      // Cache the results
      setLastFetch(now)

      const steps: WorkflowStep[] = []

      if (userRole === 'BUYER') {
        // Buyer workflow
        const poAccepted = (poStats as any).data?.accepted > 0;
        const poPending = (poStats as any).data?.pending > 0;
        const poStatus = poAccepted ? 'completed' : poPending ? 'current' : 'pending';
        
        console.log('🔍 PO Status Debug:', { poAccepted, poPending, poStatus, data: (poStats as any) });
        
        steps.push({
          id: 'purchase-orders',
          title: 'Purchase Orders',
          description: 'Create and manage orders',
          icon: <ShoppingCart className="w-5 h-5" />,
          status: poStatus,
          route: '/enterprise/post-quote-activity/purchase-orders'
        })

        const agreementSigned = (agreementStats as any).data?.signed > 0;
        const agreementPending = (agreementStats as any).data?.pending > 0;
        const agreementStatus = agreementSigned ? 'completed' : agreementPending ? 'current' : 'pending';
        
        console.log('🔍 Agreement Status Debug:', { agreementSigned, agreementPending, agreementStatus, data: (agreementStats as any) });
        
        steps.push({
          id: 'agreements',
          title: 'Digital Agreements',
          description: 'Sign contracts and documents',
          icon: <FileText className="w-5 h-5" />,
          status: agreementStatus,
          route: '/enterprise/post-quote-activity/agreements'
        })

        steps.push({
          id: 'fulfillment',
          title: 'Fulfillment',
          description: 'Track delivery and confirm receipt',
          icon: <Truck className="w-5 h-5" />,
          status: (fulfillmentStats as any).data?.delivered > 0 ? 'completed' : 
                 (fulfillmentStats as any).data?.pending > 0 || (fulfillmentStats as any).data?.shipped > 0 ? 'pending' : 
                 'pending',
          route: '/enterprise/post-quote-activity/fulfillments'
        })

        steps.push({
          id: 'invoices',
          title: 'Invoices',
          description: 'Process payments',
          icon: <CreditCard className="w-5 h-5" />,
          status: (invoiceStats as any).data?.counts?.paid > 0 ? 'completed' : 
                 (invoiceStats as any).data?.counts?.pending > 0 ? 'current' : 
                 'pending',
          route: '/enterprise/post-quote-activity/invoices'
        })

        steps.push({
          id: 'feedback',
          title: 'Feedback',
          description: 'Rate and review vendors',
          icon: <Star className="w-5 h-5" />,
          status: (fulfillmentStats as any).data?.delivered > 0 && (invoiceStats as any).data?.counts?.paid > 0 ? 'current' : 'pending',
          route: '/enterprise/post-quote-activity/feedback'
        })
      } else {
        // Vendor workflow
        steps.push({
          id: 'agreements',
          title: 'Upload Agreements',
          description: 'Upload contracts and documents',
          icon: <FileText className="w-5 h-5" />,
          status: (agreementStats as any).data?.signed > 0 ? 'completed' : 
                 (poStats as any).data?.accepted > 0 ? 'current' : 'pending',
          route: '/enterprise/post-quote-activity/agreements'
        })

        steps.push({
          id: 'fulfillment',
          title: 'Fulfillment',
          description: 'Update shipping and delivery',
          icon: <Truck className="w-5 h-5" />,
          status: (fulfillmentStats as any).data?.delivered > 0 ? 'completed' : 
                 (fulfillmentStats as any).data?.pending > 0 ? 'current' : 
                 (agreementStats as any).data?.signed > 0 ? 'current' : 'pending',
          route: '/enterprise/post-quote-activity/fulfillments'
        })

        steps.push({
          id: 'invoices',
          title: 'Invoices',
          description: 'Create and manage invoices',
          icon: <CreditCard className="w-5 h-5" />,
          status: (invoiceStats as any).data?.counts?.paid > 0 ? 'completed' : 
                 (fulfillmentStats as any).data?.delivered > 0 ? 'current' : 'pending',
          route: '/enterprise/post-quote-activity/invoices'
        })
      }

      setWorkflowSteps(steps)

      // Calculate completion percentage
      const completedSteps = steps.filter(step => step.status === 'completed').length
      const totalSteps = steps.length
      const percentage = Math.round((completedSteps / totalSteps) * 100)
      setCompletionPercentage(percentage)

      // Cache the results
      setCache({
        steps,
        completionPercentage: percentage,
        timestamp: now
      })

    } catch (error) {
      console.error('Error loading workflow progress:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStepStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-gray-700 bg-white border-gray-200'
      case 'current': return 'text-gray-700 bg-white border-red-500 workflow-pulse'
      case 'pending': return 'text-gray-400 bg-gray-50 border-gray-200'
      default: return 'text-gray-400 bg-gray-50 border-gray-200'
    }
  }

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'current': return <Clock className="w-5 h-5 text-blue-600" />
      case 'pending': return <Clock className="w-5 h-5 text-gray-400" />
      default: return <Clock className="w-5 h-5 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="flex space-x-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-8 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <style>
        {`
          @keyframes workflow-pulse {
            0% {
              border-color: rgb(239 68 68);
              box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
            }
            50% {
              border-color: rgb(220 38 38);
              box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
            }
            100% {
              border-color: rgb(239 68 68);
              box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
            }
          }
          .workflow-pulse {
            animation: workflow-pulse 1.5s infinite;
          }
        `}
      </style>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Workflow Progress</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Completion:</span>
            <span className="text-sm font-medium text-blue-600">{completionPercentage}%</span>
          </div>
        </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {workflowSteps.map((step, index) => {
          const isClickable = step.status === 'current'
          const StepComponent = isClickable ? 'button' : 'div'
          
          return (
            <StepComponent
              key={step.id}
              className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${getStepStatusColor(step.status)} ${
                isClickable ? 'cursor-pointer hover:shadow-md' : 'cursor-default'
              }`}
              onClick={isClickable ? () => navigate(step.route) : undefined}
            >
              {/* Step Number */}
              <div className="absolute -top-2 -left-2 w-6 h-6 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-gray-600">{index + 1}</span>
              </div>

              {/* Step Icon and Status */}
              <div className="flex justify-center items-center mb-3 space-x-2">
                {step.icon}
                {getStepIcon(step.status)}
              </div>

              {/* Step Title */}
              <h4 className="text-sm font-medium text-center mb-1">{step.title}</h4>
              
              {/* Step Description */}
              <p className="text-xs text-center opacity-75">{step.description}</p>

              {/* Connector Line */}
              {index < workflowSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gray-300 transform -translate-y-1/2"></div>
              )}
            </StepComponent>
          )
        })}
      </div>

      {/* Progress Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {workflowSteps.filter(step => step.status === 'completed').length} of {workflowSteps.length} steps completed
          </span>
          <span className="text-gray-600">
            {userRole === 'BUYER' ? 'Buyer Workflow' : 'Vendor Workflow'}
          </span>
        </div>
      </div>
    </div>
    </>
  )
}

export default WorkflowProgress 